/**
 * The title scene: a storm over the mansion, drawn on the 480x270 world layer with the same
 * chunky pixels as the game. Everything is a function of time so it needs no state.
 */
import { INTERNAL_W as W, INTERNAL_H as H } from '@/engine/canvas';

const SKY_TOP = '#04030a', SKY_BOTTOM = '#14112c';
const HOUSE = '#0a0814', HOUSE_RIM = '#1b1834', WINDOW = '#141226', WINDOW_LIT = '#e2b53c', WINDOW_WARM = '#8a5a1e';
const GROUND = '#0c0a15', PATH = '#15122a', FENCE = '#05040a', TREE = '#07060f';
/** The mansion stands right of centre so the menu column on the left reads over the lawn. */
const HOUSE_X = 300, HOUSE_BASE = 232;

/** 0..1 brightness of a lightning flash at time t: a double flash every eleven seconds or so. */
export function titleFlash(t: number): number {
  const cycle = 11.3, p = t % cycle;
  if (p < 0.1) return 1;
  if (p < 0.22) return 0.35;
  if (p > 0.34 && p < 0.42) return 0.8;
  if (p >= 0.42 && p < 0.9) return Math.max(0, 0.8 - (p - 0.42) * 1.7);
  return 0;
}
/** Which flash cycle t belongs to; changes exactly when a flash begins. */
export function titleFlashIndex(t: number): number {
  return Math.floor(t / 11.3);
}

const hash = (n: number) => { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); };

export function drawTitleScene(ctx: CanvasRenderingContext2D, t: number): void {
  const flash = titleFlash(t);
  // ---- sky
  const skyTop = flash ? mix(SKY_TOP, '#2a2750', flash * 0.8) : SKY_TOP;
  const skyBottom = flash ? mix(SKY_BOTTOM, '#3a3566', flash * 0.7) : SKY_BOTTOM;
  const sky = ctx.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, skyTop);
  sky.addColorStop(1, skyBottom);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, H);
  // stars
  for (let i = 0; i < 40; i++) {
    const x = Math.floor(hash(i) * W), y = Math.floor(hash(i + 100) * 110);
    const tw = 0.35 + 0.35 * Math.sin(t * (1 + hash(i + 200) * 2) + i);
    ctx.fillStyle = `rgba(200,205,235,${tw * (1 - flash)})`;
    ctx.fillRect(x, y, 1, 1);
  }
  // moon behind drifting cloud bands
  const mx = 388, my = 46;
  ctx.fillStyle = '#d8d4e6'; circle(ctx, mx, my, 15);
  ctx.fillStyle = '#c3bfd6'; circle(ctx, mx - 4, my + 3, 3); circle(ctx, mx + 5, my - 5, 2);
  ctx.fillStyle = mix(skyTop, skyBottom, my / H); circle(ctx, mx + 9, my - 8, 14); // crescent bite
  for (let i = 0; i < 4; i++) {
    const speed = 4 + i * 2.5, y = 24 + i * 16 + Math.sin(t * 0.3 + i) * 2;
    const x = ((t * speed + i * 160) % (W + 220)) - 110;
    ctx.fillStyle = `rgba(10,8,22,${0.75 - i * 0.1})`;
    roundedBand(ctx, x, y, 120 + i * 30, 9 + i * 2);
  }
  // ---- distant hills and dead trees
  ctx.fillStyle = '#080614';
  ctx.beginPath(); ctx.moveTo(0, 176); ctx.quadraticCurveTo(80, 150, 170, 172); ctx.quadraticCurveTo(300, 150, 480, 178); ctx.lineTo(W, 200); ctx.lineTo(0, 200); ctx.fill();
  tree(ctx, 42, 198, 1.0, t);
  tree(ctx, 460, 192, 0.85, t + 2);
  tree(ctx, 118, 200, 0.55, t + 4);
  // ---- the mansion
  mansion(ctx, t, flash);
  // ---- grounds: path, fence, fog
  const gg = ctx.createLinearGradient(0, 196, 0, H);
  gg.addColorStop(0, GROUND); gg.addColorStop(1, '#070610');
  ctx.fillStyle = gg; ctx.fillRect(0, 196, W, H - 196);
  ctx.fillStyle = PATH;
  ctx.beginPath(); ctx.moveTo(HOUSE_X - 14, HOUSE_BASE + 2); ctx.lineTo(HOUSE_X + 14, HOUSE_BASE + 2); ctx.lineTo(HOUSE_X + 40, H); ctx.lineTo(HOUSE_X - 40, H); ctx.fill();
  ctx.fillStyle = '#1b1731';
  for (let i = 0; i < 5; i++) { const y = HOUSE_BASE + 6 + i * 7, s = 16 + i * 5; ctx.fillRect(HOUSE_X - s, y, s * 2, 1); }
  // fog bands drifting across the lawn
  for (let i = 0; i < 3; i++) {
    const x = ((t * (6 + i * 4) + i * 200) % (W + 300)) - 150;
    ctx.fillStyle = `rgba(90,86,140,${0.06 + i * 0.02 + flash * 0.05})`;
    roundedBand(ctx, x, 206 + i * 14, 220, 10);
    roundedBand(ctx, x - 260, 212 + i * 14, 180, 8);
  }
  fence(ctx, HOUSE_X);
  // ---- rain, angled by the wind
  const wind = 0.35 + 0.25 * Math.sin(t * 0.4);
  ctx.strokeStyle = `rgba(150,160,210,${0.18 + flash * 0.25})`;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 0; i < 90; i++) {
    const speed = 300 + hash(i + 300) * 160, len = 5 + hash(i + 400) * 5;
    const x = (hash(i) * (W + 80) - 40 + t * speed * wind * 0.3) % (W + 80) - 40;
    const y = (hash(i + 500) * H + t * speed) % (H + 20) - 10;
    ctx.moveTo(x, y); ctx.lineTo(x - len * wind, y + len);
  }
  ctx.stroke();
  // vignette
  const v = ctx.createRadialGradient(W / 2, H / 2, H * 0.35, W / 2, H / 2, W * 0.72);
  v.addColorStop(0, 'rgba(0,0,0,0)'); v.addColorStop(1, 'rgba(0,0,0,0.6)');
  ctx.fillStyle = v; ctx.fillRect(0, 0, W, H);
}

function mansion(ctx: CanvasRenderingContext2D, t: number, flash: number): void {
  const cx = HOUSE_X, base = HOUSE_BASE;
  const body = flash ? mix(HOUSE, '#2c2850', flash * 0.55) : HOUSE;
  const rim = flash ? mix(HOUSE_RIM, '#6b66a8', flash * 0.7) : HOUSE_RIM;
  ctx.fillStyle = body;
  // wings
  ctx.fillRect(cx - 150, base - 56, 90, 56);
  ctx.fillRect(cx + 60, base - 56, 90, 56);
  // main block
  ctx.fillRect(cx - 70, base - 82, 140, 82);
  // gabled roofs on the wings
  gable(ctx, cx - 150, cx - 60, base - 56, 26, body, rim);
  gable(ctx, cx + 60, cx + 150, base - 56, 26, body, rim);
  // main roof: a mansard slab with a rim
  ctx.fillStyle = body;
  ctx.beginPath(); ctx.moveTo(cx - 74, base - 82); ctx.lineTo(cx - 58, base - 100); ctx.lineTo(cx + 58, base - 100); ctx.lineTo(cx + 74, base - 82); ctx.fill();
  ctx.fillStyle = rim; ctx.fillRect(cx - 58, base - 100, 116, 1);
  // central tower with a spire
  ctx.fillStyle = body; ctx.fillRect(cx - 16, base - 112, 32, 32);
  ctx.beginPath(); ctx.moveTo(cx - 19, base - 112); ctx.lineTo(cx, base - 132); ctx.lineTo(cx + 19, base - 112); ctx.fill();
  ctx.fillStyle = rim; ctx.fillRect(cx - 1, base - 138, 1, 7); ctx.fillRect(cx - 19, base - 112, 38, 1);
  // chimneys
  ctx.fillStyle = body; ctx.fillRect(cx - 120, base - 96, 8, 20); ctx.fillRect(cx + 104, base - 92, 7, 16);
  // moonlit rim along the top edges; lightning strengthens it
  {
    ctx.fillStyle = `rgba(150,146,210,${0.22 + flash * 0.6})`;
    ctx.fillRect(cx - 70, base - 82, 140, 1); ctx.fillRect(cx - 150, base - 56, 90, 1); ctx.fillRect(cx + 60, base - 56, 90, 1);
    ctx.fillRect(cx - 16, base - 112, 32, 1);
  }
  // windows: three rows on the main block, two on the wings
  const win = (x: number, y: number, w: number, h: number, lit: 0 | 1 | 2) => {
    ctx.fillStyle = lit === 2 ? WINDOW_LIT : lit === 1 ? WINDOW_WARM : WINDOW;
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = lit ? 'rgba(255,230,184,0.35)' : 'rgba(40,36,70,0.9)';
    ctx.fillRect(x, y, w, 1);
    if (h > 8) { ctx.fillStyle = lit ? 'rgba(90,60,20,0.6)' : 'rgba(6,5,12,0.9)'; ctx.fillRect(x + Math.floor(w / 2), y + 1, 1, h - 1); ctx.fillRect(x, y + Math.floor(h / 2), w, 1); }
  };
  const candle = Math.sin(t * 9) * Math.sin(t * 3.7) > -0.2;
  for (let r = 0; r < 3; r++) for (let c = 0; c < 5; c++) {
    if (r === 2 && c === 2) continue; // the door
    const lit: 0 | 1 | 2 = r === 0 && c === 3 ? (candle ? 2 : 1) : 0;
    win(cx - 58 + c * 26, base - 74 + r * 26, 10, 14, lit);
  }
  for (let r = 0; r < 2; r++) for (let c = 0; c < 3; c++) {
    win(cx - 138 + c * 28, base - 48 + r * 24, 10, 12, 0);
    win(cx + 72 + c * 28, base - 48 + r * 24, 10, 12, 0);
  }
  // tower window: dark, but every so often two eyes catch the light
  win(cx - 5, base - 106, 10, 12, 0);
  const eyesOn = (t % 9) > 6.2 && (t % 9) < 7.6 && Math.floor(t * 14) % 9 !== 0;
  if (eyesOn) { ctx.fillStyle = '#ffd23a'; ctx.fillRect(cx - 3, base - 101, 2, 2); ctx.fillRect(cx + 1, base - 101, 2, 2); }
  // porch: columns, steps and the door itself
  ctx.fillStyle = body; ctx.fillRect(cx - 24, base - 30, 48, 30);
  ctx.fillStyle = rim; ctx.fillRect(cx - 24, base - 30, 48, 1);
  ctx.fillStyle = '#100d1c'; ctx.fillRect(cx - 20, base - 29, 3, 29); ctx.fillRect(cx + 17, base - 29, 3, 29);
  ctx.fillStyle = '#050409'; ctx.fillRect(cx - 7, base - 24, 14, 24);
  ctx.fillStyle = '#2a2440'; ctx.fillRect(cx + 3, base - 12, 1, 1);
  ctx.fillStyle = '#12101f'; ctx.fillRect(cx - 14, base, 28, 2); ctx.fillRect(cx - 18, base + 2, 36, 2);
  // a faint warm spill from the lit window
  if (candle) {
    const wx = cx - 58 + 3 * 26 + 5, wy = base - 74 + 7;
    const glow = ctx.createRadialGradient(wx, wy, 2, wx, wy, 26);
    glow.addColorStop(0, 'rgba(226,181,60,0.16)'); glow.addColorStop(1, 'rgba(226,181,60,0)');
    ctx.fillStyle = glow; ctx.fillRect(wx - 26, wy - 26, 52, 52);
  }
}

function gable(ctx: CanvasRenderingContext2D, x0: number, x1: number, y: number, h: number, body: string, rim: string): void {
  ctx.fillStyle = body;
  ctx.beginPath(); ctx.moveTo(x0 - 4, y); ctx.lineTo((x0 + x1) / 2, y - h); ctx.lineTo(x1 + 4, y); ctx.fill();
  ctx.strokeStyle = rim; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(x0 - 4, y + 0.5); ctx.lineTo((x0 + x1) / 2, y - h + 0.5); ctx.stroke();
}

function tree(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, t: number): void {
  const sway = Math.sin(t * 1.3) * 1.5 * s;
  ctx.strokeStyle = TREE; ctx.lineCap = 'round';
  const limb = (x0: number, y0: number, x1: number, y1: number, w: number) => { ctx.lineWidth = w; ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke(); };
  limb(x, y, x + sway * 0.3, y - 34 * s, 4 * s);
  limb(x + sway * 0.3, y - 30 * s, x - 16 * s + sway, y - 52 * s, 2.5 * s);
  limb(x + sway * 0.3, y - 34 * s, x + 14 * s + sway, y - 56 * s, 2.5 * s);
  limb(x - 10 * s + sway * 0.7, y - 44 * s, x - 24 * s + sway, y - 50 * s, 1.5 * s);
  limb(x + 8 * s + sway * 0.7, y - 46 * s, x + 20 * s + sway, y - 66 * s, 1.5 * s);
  limb(x + 12 * s + sway, y - 54 * s, x + 4 * s + sway, y - 70 * s, 1.2 * s);
  limb(x - 14 * s + sway, y - 50 * s, x - 10 * s + sway, y - 64 * s, 1.2 * s);
}

function fence(ctx: CanvasRenderingContext2D, gate: number): void {
  const y = 240;
  ctx.fillStyle = FENCE;
  for (const [x0, x1] of [[0, gate - 40], [gate + 40, W]]) {
    ctx.fillRect(x0, y + 6, x1 - x0, 2);
    ctx.fillRect(x0, y + 20, x1 - x0, 2);
    for (let x = x0 + 3; x < x1; x += 7) {
      ctx.fillRect(x, y, 1, 34);
      ctx.fillRect(x - 1, y - 2, 3, 2); ctx.fillRect(x, y - 4, 1, 2);
    }
  }
  // gate posts
  ctx.fillRect(gate - 46, y - 10, 6, 44); ctx.fillRect(gate + 40, y - 10, 6, 44);
  ctx.fillStyle = '#1a1730'; ctx.fillRect(gate - 46, y - 10, 6, 1); ctx.fillRect(gate + 40, y - 10, 6, 1);
}

function circle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
}
function roundedBand(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  ctx.beginPath(); ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2); ctx.fill();
}
/** Blend two colours; accepts and returns #rrggbb so results can be blended again. */
function mix(a: string, b: string, k: number): string {
  const pa = hex(a), pb = hex(b);
  return '#' + pa.map((v, i) => Math.round(v + (pb[i] - v) * k).toString(16).padStart(2, '0')).join('');
}
function hex(h: string): number[] {
  const s = h.replace('#', '');
  return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
}
